<template>
  <div>
    <div class="grid lg:grid-cols-envelopesPage gap-twentyFive">
      <div class="lg:row-envelopesPageMain min-w-0">
        <EnvelopeSearch
          v-if="results.length || searchWithNoResult"
          @onSearch="handleNewSearch"
          :initialQuery="$route.query"
        />
        <EnvelopesPageHeader
          v-if="results.length || searchWithNoResult"
          @statusesUpdated="handleNewStatus"
          :loading="isLoading"
          :envelopeTotal="meta ? meta.total : undefined"
          :aggregates="meta ? meta.aggregates : undefined"
        />
        <template v-if="isLoading">
          <EnvelopeOverviewSkeleton
            v-for="skeleton in skeletonArray"
            :key="skeleton.fingerprint"
            pulse
          />
        </template>
        <TransitionGroup
          v-else
          tag="div"
          move-class="transition-all delay-200 duration-200 ease"
          enter-active-class="transition-all delay-200 duration-200 ease"
          leave-active-class="transition-all delay-200 duration-200 ease absolute"
          enter-from-class="opacity-0"
          leave-to-class="opacity-0 translate-x-lg"
        >
          <EnvelopeOverviewCard
            v-for="envelope in results"
            :key="envelope.fingerprint"
            :envelope="envelope"
            ec="Envelopes"
            @cancel="openCancelEnvelopeModal(envelope)"
            @remove="openRemoveEnvelopeModal(envelope)"
            @openReminder="openRemindersSetModal(envelope)"
            @openResendNowModal="openResendNowModal(envelope)"
          />
        </TransitionGroup>
        <NoAssociatedEnvelopesEmptyState v-if="searchWithNoResult" />
        <NoEnvelopesEmptyState v-else-if="!results.length && !isLoading" />
        <NumberedPaginator
          v-if="meta && results.length"
          class="mt-lg lg:pb-lg"
          :meta="meta"
          resource="envelopes"
          @page="handlePagination"
          pageDataEc="Envelopes"
        />
      </div>
      <VerificationRequiredWidget class="col-start-1 col-end-2 lg:col-start-2 lg:col-end-3" />
      <EnvelopeUsageWidget class="col-start-1 col-end-2 lg:col-start-2 lg:col-end-3" />
    </div>
    <EnvelopeCancelModal
      :show="showEnvelopeCancelModal"
      :envelope="selectedEnvelope"
      @close="showEnvelopeCancelModal = false;"
      @envelopeCancelled="markEnvelopeCancelled(selectedEnvelope.fingerprint, statusParams)"
    />
    <EnvelopeDeleteModal
      :show="showEnvelopeDeleteModal"
      :envelope="selectedEnvelope"
      @close="showEnvelopeDeleteModal = false;"
      @envelopeDeleted="markEnvelopeDeleted(selectedEnvelope.fingerprint)"
    />
    <EnvelopeResendNowModal
      :envelope="selectedEnvelope"
      :show="showEnvelopeResendModal"
      @close="showEnvelopeResendModal = false"
    />
  </div>
</template>

<script>
import { useEnvelopesList } from '@/composables/envelopes';

import EnvelopeOverviewCard from '@/components/ui/envelopes/EnvelopeOverviewCard.vue';
import EnvelopeOverviewSkeleton from '@/components/ui/envelopes/EnvelopeOverviewSkeleton.vue';
import EnvelopeCancelModal from '@/views/modals/envelope/EnvelopeCancelModal.vue';
import EnvelopeResendNowModal from '@/views/modals/envelope/EnvelopeResendNowModal.vue';
import EnvelopeDeleteModal from '@/views/modals/envelope/EnvelopeDeleteModal.vue';
import EnvelopesPageHeader from '@/components/ui/envelopes/EnvelopesPageHeader.vue';
import { NoEnvelopesEmptyState } from '@/components/ui/envelopes/emptyStates/noEnvelopes';
import { NoAssociatedEnvelopesEmptyState } from '@/components/ui/envelopes/emptyStates/noAssociatedEnvelopes';
import NumberedPaginator from '@/components/ui/pagination/Paginator.vue';
import EnvelopeSearch from '@/views/components/EnvelopeSearch.vue';
import EnvelopeUsageWidget from '@/components/ui/envelopes/EnvelopeUsageWidget.vue';
import VerificationRequiredWidget from '@/components/ui/widgets/VerificationRequiredWidget.vue';

export default {
  name: 'EnvelopesPage',
  components: {
    NoEnvelopesEmptyState,
    NoAssociatedEnvelopesEmptyState,
    EnvelopeOverviewCard,
    EnvelopeOverviewSkeleton,
    NumberedPaginator,
    EnvelopeCancelModal,
    EnvelopesPageHeader,
    EnvelopeSearch,
    EnvelopeUsageWidget,
    EnvelopeResendNowModal,
    EnvelopeDeleteModal,
    VerificationRequiredWidget,
  },
  setup() {
    const {
      fetch,
      results,
      meta,
      isLoading,
      refresh,
      markEnvelopeDeleted,
      markEnvelopeCancelled,
    } = useEnvelopesList();

    return {
      fetch,
      results,
      meta,
      isLoading,
      refresh,
      markEnvelopeDeleted,
      markEnvelopeCancelled,
    };
  },
  data() {
    return {
      searchParams: {},
      statusParams: [],
      selectedEnvelope: {},
      showEnvelopeCancelModal: false,
      showEnvelopeResendModal: false,
      showEnvelopeDeleteModal: false,
    };
  },
  computed: {
    skeletonArray() {
      return Array.from({ length: 4 }, (_, index) => ({ fingerprint: index }));
    },
    searchWithNoResult() {
      const hasResults = !this.results.length;
      const hasSearchParams = this.searchParams.query ||
        this.searchParams.sender_id ||
        this.searchParams.created_at ||
        this.searchParams.contact_id ||
        this.searchParams.bulk_send_fingerprint ||
        this.statusParams.length;

      return hasResults && hasSearchParams;
    },
  },
  created() {
    if (this.$route.query.bulk_send_fingerprint) {
      const searchQuery = {
        bulk_send_fingerprint: {
          inc: [this.$route.query.bulk_send_fingerprint],
        },
      };
      this.searchParams = searchQuery;
    }

    this.setDefaultPage();
    this.doEnvelopeSearch();
  },
  methods: {
    setDefaultPage() {
      if (this.$route.query.page && this.$route.query.page !== '1') {
        this.$router.replace({ query: {} });
      }
    },
    handleNewSearch(params) {
      this.searchParams = params;
      this.setDefaultPage();
      this.doEnvelopeSearch();
    },
    handleNewStatus(params) {
      this.statusParams = params;
      this.setDefaultPage();
      this.doEnvelopeSearch();
    },
    doEnvelopeSearch() {
      const params = {
        status: { inc: this.statusParams },
        ...this.searchParams,
        page: this.$route.query.page ? this.$route.query.page : 1,
      };
      this.fetch(params);
    },
    handlePagination(url) {
      const parsedUrl = new URL(url);
      const page = parsedUrl.searchParams.get('page');

      if (this.$route.query.page !== page) {
        this.$router.replace({ query: { page } });
      }
      this.doEnvelopeSearch();
    },
    handleDeleteEnvelope() {
      this.showEnvelopeDeleteModal = false;
      this.refresh();
    },
    setSelectedEnvelope(envelope) {
      this.selectedEnvelope = envelope;
    },
    openRemindersSetModal(envelope) {
      this.setSelectedEnvelope(envelope);
      this.$bus.$emit('toggleModal', {
        name: 'reminders-set-modal',
        active: true,
      });
    },
    openResendNowModal(envelope) {
      this.setSelectedEnvelope(envelope);
      this.showEnvelopeResendModal = true;
    },
    openCancelEnvelopeModal(envelope) {
      this.setSelectedEnvelope(envelope);
      this.showEnvelopeCancelModal = true;
    },
    openRemoveEnvelopeModal(envelope) {
      this.setSelectedEnvelope(envelope);
      this.showEnvelopeDeleteModal = true;
    },
  },
};
</script>
